import React, { useMemo, useState, useTransition } from 'react';
import PlatformAvatar from './PlatformAvatar';
import PlatformButton from './PlatformButton';
import { WORKSPACE_ROLES } from '../lib/permissions';

function TeamSection({
  members = [],
  pendingInvites = [],
  companyID = '',
  onInvite,
  onUpdateRole,
  onRemove,
  onRevokeInvite,
}) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(WORKSPACE_ROLES.MEMBER);
  const [error, setError] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [copyButtonText, setCopyButtonText] = useState('Copy link');
  const [pending, startTransition] = useTransition();

  const totalCount = members.length + pendingInvites.length;

  const teamInviteLink = useMemo(() => {
    if (!companyID) return '';
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://www.nurturer.ai';
    return `${origin}/register/${companyID}`;
  }, [companyID]);

  const runAction = (action) => {
    setError(null);
    startTransition(async () => {
      const result = await action();
      if (!result?.ok) {
        setError(result?.error || 'Something went wrong.');
      }
    });
  };

  const handleCopyTeamLink = async () => {
    if (!teamInviteLink) {
      setError('Company ID is missing, so an invite link cannot be created.');
      return;
    }
    try {
      await navigator.clipboard.writeText(teamInviteLink);
      setCopyButtonText('Copied');
      setTimeout(() => setCopyButtonText('Copy link'), 2500);
    } catch (_) {
      setError('Could not copy invite link.');
    }
  };

  const handleInvite = (e) => {
    e.preventDefault();
    setError(null);
    setInviteLink(null);
    startTransition(async () => {
      const result = await onInvite?.({ email, role });
      if (!result?.ok) {
        setError(result?.error || 'Could not invite.');
        return;
      }
      setEmail('');
      setRole(WORKSPACE_ROLES.MEMBER);
      if (result.error) {
        setError(result.error);
      }
      if (result.invitePath) {
        const absolute = `${window.location.origin}${result.invitePath}`;
        setInviteLink(absolute);
        try {
          await navigator.clipboard.writeText(absolute);
        } catch (_) {
          /* ignore */
        }
      }
    });
  };

  return (
    <div className="platform-card settings-card" id="team">
      <div className="platform-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Team · {totalCount}</h3>
        <PlatformButton size="sm" variant="primary" type="button" onClick={() => setInviteOpen(true)}>
          + Invite
        </PlatformButton>
      </div>

      <div className="settings-team-share">
        <div className="settings-team-share-label">Shareable invite link</div>
        <p className="settings-team-share-copy">
          Anyone with this link can register and join your workspace. For a specific person and role,
          use Invite instead.
        </p>
        <div className="settings-invite-row">
          <input className="platform-input" value={teamInviteLink || '—'} readOnly />
          <PlatformButton
            size="sm"
            variant="outline"
            type="button"
            disabled={!teamInviteLink}
            onClick={handleCopyTeamLink}
          >
            {copyButtonText}
          </PlatformButton>
        </div>
      </div>

      {error && (
        <p className="settings-inline-error" role="alert">
          {error}
        </p>
      )}

      {inviteOpen && (
        <form className="settings-invite-form" onSubmit={handleInvite}>
          <input
            className="platform-input"
            type="email"
            placeholder="colleague@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <select
            className="platform-input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value={WORKSPACE_ROLES.MEMBER}>Member</option>
            <option value={WORKSPACE_ROLES.ADMIN}>Admin</option>
          </select>
          <PlatformButton size="sm" variant="primary" type="submit" disabled={pending}>
            Send invite
          </PlatformButton>
          <PlatformButton
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => {
              setInviteOpen(false);
              setInviteLink(null);
            }}
          >
            Cancel
          </PlatformButton>
        </form>
      )}

      {inviteLink && (
        <p className="settings-invite-link">
          Invite email sent. Backup link (also copied if clipboard allowed):
          <br />
          <code>{inviteLink}</code>
        </p>
      )}

      {members.map((m) => (
        <div key={m.id} className="settings-team-row">
          <PlatformAvatar name={m.name} size={36} photoUrl={m.photoUrl} />
          <div className="settings-team-meta">
            <div className="settings-team-name">
              {m.name}
              {m.isYou && <span className="settings-team-you"> · you</span>}
            </div>
            <div className="settings-team-email">{m.email}</div>
          </div>
          {!m.isYou ? (
            <div className="settings-team-actions">
              <select
                className="platform-input settings-role-select"
                value={m.role}
                disabled={pending}
                onChange={(e) =>
                  runAction(() => onUpdateRole?.(m.id, e.target.value))
                }
              >
                <option value={WORKSPACE_ROLES.ADMIN}>Admin</option>
                <option value={WORKSPACE_ROLES.MEMBER}>Member</option>
              </select>
              <button
                type="button"
                className="settings-remove-btn"
                disabled={pending}
                onClick={() => runAction(() => onRemove?.(m.id))}
              >
                Remove
              </button>
            </div>
          ) : (
            <span className={`settings-role-badge ${m.role === 'ADMIN' ? 'admin' : ''}`}>
              {m.roleLabel}
            </span>
          )}
        </div>
      ))}

      {pendingInvites.map((inv) => (
        <div key={inv.id} className="settings-team-row">
          <PlatformAvatar name={inv.email} size={36} />
          <div className="settings-team-meta">
            <div className="settings-team-name">
              {inv.email.split('@')[0]}
              <span className="settings-team-pending"> · pending</span>
            </div>
            <div className="settings-team-email">{inv.email}</div>
          </div>
          <div className="settings-team-actions">
            <span className="settings-role-badge">{inv.roleLabel}</span>
            <button
              type="button"
              className="settings-remove-btn"
              disabled={pending}
              onClick={() => runAction(() => onRevokeInvite?.(inv.id))}
            >
              Revoke
            </button>
          </div>
        </div>
      ))}

      {members.length === 0 && pendingInvites.length === 0 && (
        <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '12px 0 0' }}>
          No teammates yet. Share the invite link or invite someone by email.
        </p>
      )}
    </div>
  );
}

export default TeamSection;
